// packages/ui-kit/src/primitives/Switch.tsx
// C3F v18 — reference: design/system/components/forms/Switch.{jsx,prompt.md}
// A switch is for settings that take effect IMMEDIATELY. If the screen has a
// "Save" button, use Checkbox instead.
import { type ChangeEvent, type ReactNode, forwardRef, useId } from "react";

interface Props {
  checked?: boolean;
  onChange?: (checked: boolean, e: ChangeEvent<HTMLInputElement>) => void;
  label?: ReactNode;
  /** Secondary line under the label (muted caption). */
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
  className?: string;
  /**
   * Inoltrato all'input, non allo scatolotto.
   *
   * Serve dichiararlo: TypeScript non controlla gli attributi JSX che
   * contengono un trattino, quindi `data-testid` su un componente compila
   * sempre e sparisce in silenzio se nessuno lo inoltra. Due schermate lo
   * passavano già credendo di avere un aggancio che non esisteva.
   */
  "data-testid"?: string;
}

/**
 * Shared switch — 46×28 pill track over a visually-hidden native checkbox
 * with `role="switch"`. Off track = --border-control (WCAG 1.4.11 3:1),
 * on = accent fill; the thumb is DS-fixed white in both themes.
 */
export const Switch = forwardRef<HTMLInputElement, Props>(function Switch(
  {
    checked = false,
    onChange,
    label,
    description,
    disabled,
    id,
    className = "",
    "data-testid": testId,
  },
  ref,
) {
  const autoId = useId();
  const sid = id ?? autoId;
  const control = (
    <span className="relative inline-flex h-7 w-[46px] shrink-0 items-center">
      <input
        ref={ref}
        id={sid}
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked, e)}
        {...(testId !== undefined ? { "data-testid": testId } : {})}
        className="peer absolute inset-0 m-0 h-7 w-[46px] cursor-[inherit] opacity-0"
      />
      <span
        aria-hidden
        className="pointer-events-none h-7 w-[46px] rounded-pill bg-border-control transition-colors ease-out peer-checked:bg-accent peer-focus-visible:shadow-focus peer-disabled:opacity-45"
      />
      {/* Thumb: DS-fixed white + its hardcoded shadow (same in both themes). */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-[3px] h-[22px] w-[22px] rounded-full bg-white shadow-[0_1px_3px_rgba(16,13,22,.3)] transition-transform ease-out peer-checked:translate-x-[18px]"
      />
    </span>
  );
  if (label == null && description == null) {
    return <span className={className}>{control}</span>;
  }
  return (
    <label
      htmlFor={sid}
      className={`flex min-h-touch items-center gap-3.5 ${
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      } ${className}`}
    >
      {control}
      {/* opacity here is the disabled-control exception, not text dimming */}
      <span className={`min-w-0 ${disabled ? "opacity-45" : ""}`}>
        {label != null && <span className="text-sm font-medium text-fg-default">{label}</span>}
        {description != null && (
          <span className="mt-0.5 block text-caption text-fg-muted">{description}</span>
        )}
      </span>
    </label>
  );
});
